/**
 * The copilot's streaming client.
 *
 * `POST /ai/conversations/:conversationId/messages` answers JSON by default, and that path is the
 * generated ts-rest client — `createApiClient(...).ai.sendMessage` — which needs nothing from this
 * file. With `Accept: text/event-stream` the same route streams the turn as Server-Sent Events,
 * and THAT needs a hand-written client for two reasons: `EventSource` can neither POST nor send a
 * bearer, and the ts-rest fetcher reads the whole body before returning. So: `authedFetch` (the
 * typed client's bearer and single-flight refresh, shared) plus a small SSE parser.
 *
 * Nothing else the copilot UI does needs a helper here. `approveProposal`, `rejectProposal`,
 * `getTurn` (catch-up after a dropped stream), `getAllowance` and the rest are ordinary JSON
 * routes the typed client and the TanStack bindings already cover; a wrapper per route would be
 * a second place for their types to live, and the contract is meant to be the only one.
 *
 * Universal: browser, React Native and Node. No DOM, no Node built-ins — `fetch`, `TextDecoder`
 * and `ReadableStream` are the WHATWG globals all three provide. Note that React Native's built-in
 * `fetch` buffers response bodies (`response.body` is undefined); Expo passes `expo/fetch`, which
 * streams.
 */

import {
  aiSseEventSchema,
  contract,
  errorBodySchema,
  sendMessageResponseSchema,
  type AiBlock,
  type AiProposal,
  type AiSseEvent,
  type SendMessageBody,
} from '@hrms/shared';
import { authedFetch, type AuthedFetchOptions } from './client.js';

// ---------------------------------------------------------------------------
// SSE parsing
// ---------------------------------------------------------------------------

export interface SseRawEvent {
  event: string;
  data: string;
  id?: string;
}

export interface SseParser {
  push(chunk: string): void;
  /** The stream is over: a final event the server never followed with a blank line is delivered. */
  end(): void;
}

/**
 * The WHATWG event-stream format, incrementally: feed chunks as they arrive, get events as they
 * complete. Incremental because a chunk boundary can land anywhere — mid-`data:` line, or between
 * the `\r` and `\n` of a CRLF — and a parser that assumed whole lines per chunk would work on
 * localhost and corrupt text through a proxy that re-chunks.
 *
 * Handles LF, CR and CRLF line endings; multi-line `data:` (joined with `\n`, as the spec says);
 * `:` comment lines (the server's `: ping` keep-alives); and `id:`. `retry:` is ignored — there
 * is no reconnect here, a dropped stream is caught up via `getTurn`.
 */
export function createSseParser(onEvent: (event: SseRawEvent) => void): SseParser {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];
  let id: string | undefined;

  function dispatch(): void {
    // No `data:` means no event, per spec — a lone `event:` line is discarded, not delivered empty.
    if (dataLines.length === 0) {
      eventName = '';
      return;
    }
    const event: SseRawEvent = { event: eventName || 'message', data: dataLines.join('\n') };
    if (id !== undefined) event.id = id;
    eventName = '';
    dataLines = [];
    onEvent(event);
  }

  function processLine(line: string): void {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    // Exactly one leading space is separator, not content: `data:  x` carries " x".
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'event':
        eventName = value;
        break;
      case 'data':
        dataLines.push(value);
        break;
      case 'id':
        id = value;
        break;
      default:
        break;
    }
  }

  return {
    push(chunk) {
      buffer += chunk;
      let start = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const char = buffer[i];
        if (char === '\n') {
          processLine(buffer.slice(start, i));
          start = i + 1;
        } else if (char === '\r') {
          // A trailing `\r` may be the first half of a CRLF split across chunks: keep it.
          if (i + 1 === buffer.length) break;
          processLine(buffer.slice(start, i));
          if (buffer[i + 1] === '\n') i += 1;
          start = i + 1;
        }
      }
      buffer = buffer.slice(start);
    },
    end() {
      if (buffer !== '') {
        processLine(buffer.endsWith('\r') ? buffer.slice(0, -1) : buffer);
        buffer = '';
      }
      dispatch();
    },
  };
}

/**
 * Reads a response body to the end, delivering each event as it completes. Decodes with
 * `{ stream: true }` so a multi-byte character split across chunks — common with Devanagari or an
 * emoji in a delta — is not rendered as U+FFFD. Aborting the signal cancels the read; the returned
 * promise then resolves (the reader reports done) or rejects with the fetch's own AbortError,
 * depending on the platform, so the caller checks `signal.aborted` rather than the outcome.
 */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SseRawEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser(onEvent);

  // Cancelling resolves a pending read() as done, instead of leaving it hanging until the server
  // notices the closed socket — which for a keep-alive stream is never.
  const cancel = () => void reader.cancel().catch(() => undefined);
  if (signal?.aborted) {
    cancel();
    return;
  }
  signal?.addEventListener('abort', cancel, { once: true });

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
    parser.end();
  } catch (error) {
    // A handler that throws must not leave the connection open behind it.
    cancel();
    throw error;
  } finally {
    signal?.removeEventListener('abort', cancel);
  }
}

// ---------------------------------------------------------------------------
// The streaming send
// ---------------------------------------------------------------------------

export interface StreamCopilotMessageOptions extends AuthedFetchOptions {
  conversationId: string;
  body: SendMessageBody;
  signal?: AbortSignal;
  /** Validated events only, in stream order. */
  onEvent: (event: AiSseEvent) => void;
  /**
   * An event that failed `aiSseEventSchema` — a server ahead of this client, most likely. It is
   * skipped, not fatal: the turn is still running and `done` will still arrive.
   */
  onMalformed?: (raw: SseRawEvent, error: unknown) => void;
}

export type CopilotDoneData = Extract<AiSseEvent, { event: 'done' }>['data'];

/**
 * Every way a send can end, as a value. Callers branch on `status` rather than on exception type,
 * because most of these are ordinary — an exhausted allowance, a cancelled send — and a UI handles
 * them with a message, not a crash. Codes the client synthesises itself: `NETWORK_ERROR` (no
 * response at all), `HTTP_ERROR` (a non-2xx without a parseable error body), `STREAM_UNSUPPORTED`
 * (this `fetch` cannot stream), `STREAM_ENDED` (the body ended before `done`, so the caller
 * catches up via `getTurn`), `INVALID_RESPONSE`, `TURN_FAILED`.
 */
export type StreamCopilotMessageResult =
  | { status: 'done'; data: CopilotDoneData }
  | { status: 'error'; code: string; message: string; httpStatus?: number }
  | { status: 'aborted' };

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * The JSON answer, reshaped into what the stream would have ended with. A proxy that buffers or
 * strips streaming (some corporate ones do) makes the API answer JSON; callers must not need a
 * second code path for that, so the `done` event is synthesised and delivered like any other.
 */
function fromJsonResponse(
  json: unknown,
  onEvent: StreamCopilotMessageOptions['onEvent'],
): StreamCopilotMessageResult {
  const parsed = sendMessageResponseSchema.safeParse(json);
  if (!parsed.success) {
    return {
      status: 'error',
      code: 'INVALID_RESPONSE',
      message: 'The server answered with something other than a copilot turn',
    };
  }

  const { turn, messages, proposals } = parsed.data;
  if (turn.status === 'error') {
    const data = { code: 'TURN_FAILED', message: turn.error ?? 'The turn failed' };
    onEvent({ event: 'error', data });
    return { status: 'error', ...data };
  }

  const message = messages.filter((m) => m.role === 'assistant').at(-1);
  if (!message) {
    return {
      status: 'error',
      code: 'INVALID_RESPONSE',
      message: 'The turn finished without an assistant message',
    };
  }

  const data: CopilotDoneData = { turn, message, proposals };
  onEvent({ event: 'done', data });
  return { status: 'done', data };
}

/**
 * Send a message and stream the turn. Resolves when `done` or `error` arrives, when the body ends,
 * or when the signal aborts — never rejects for anything the network or the server did.
 */
export async function streamCopilotMessage(
  options: StreamCopilotMessageOptions,
): Promise<StreamCopilotMessageResult> {
  const { signal, onEvent } = options;

  // From the contract, not a literal — the same reason the typed client exists.
  const path = contract.ai.sendMessage.path.replace(
    ':conversationId',
    encodeURIComponent(options.conversationId),
  );

  let response: Response;
  try {
    response = await authedFetch(options, `${options.baseUrl}${path}`, {
      method: 'POST',
      headers: { accept: 'text/event-stream', 'content-type': 'application/json' },
      body: JSON.stringify(options.body),
      signal,
    });
  } catch (error) {
    if (signal?.aborted) return { status: 'aborted' };
    return { status: 'error', code: 'NETWORK_ERROR', message: messageOf(error) };
  }

  if (!response.ok) {
    const problem = errorBodySchema.safeParse(await response.json().catch(() => undefined));
    return problem.success
      ? {
          status: 'error',
          code: problem.data.code,
          message: problem.data.message,
          httpStatus: response.status,
        }
      : {
          status: 'error',
          code: 'HTTP_ERROR',
          message: `The server answered ${response.status}`,
          httpStatus: response.status,
        };
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.startsWith('text/event-stream')) {
    return fromJsonResponse(await response.json().catch(() => undefined), onEvent);
  }

  if (!response.body) {
    return {
      status: 'error',
      code: 'STREAM_UNSUPPORTED',
      message: 'This fetch cannot stream response bodies; on React Native pass expo/fetch',
    };
  }

  // `null as …` keeps the declared type wide: these are assigned from inside `handleRaw`, which
  // TypeScript's flow analysis cannot see, and a plain `= null` would narrow them to `null` for
  // good.
  let outcome = null as StreamCopilotMessageResult | null;
  // A throwing `onEvent` is a bug in the caller, surfaced as such — not disguised as the stream
  // having dropped.
  let handlerError = null as { error: unknown } | null;

  const handleRaw = (raw: SseRawEvent): void => {
    if (outcome) return;

    let json: unknown;
    try {
      json = JSON.parse(raw.data);
    } catch (error) {
      options.onMalformed?.(raw, error);
      return;
    }
    const parsed = aiSseEventSchema.safeParse({ event: raw.event, data: json });
    if (!parsed.success) {
      options.onMalformed?.(raw, parsed.error);
      return;
    }

    const event = parsed.data;
    if (event.event === 'done') outcome = { status: 'done', data: event.data };
    else if (event.event === 'error') outcome = { status: 'error', ...event.data };

    try {
      onEvent(event);
    } catch (error) {
      handlerError = { error };
      throw error;
    }
  };

  try {
    await readSseStream(response.body, handleRaw, signal);
  } catch (error) {
    if (handlerError) throw handlerError.error;
    if (signal?.aborted) return { status: 'aborted' };
    // A drop mid-stream. The turn keeps running server-side; the caller catches up via getTurn.
    return outcome ?? { status: 'error', code: 'STREAM_ENDED', message: messageOf(error) };
  }

  if (outcome) return outcome;
  if (signal?.aborted) return { status: 'aborted' };
  return {
    status: 'error',
    code: 'STREAM_ENDED',
    message: 'The stream ended before the turn finished',
  };
}

// ---------------------------------------------------------------------------
// Reducing events into something a screen can render
// ---------------------------------------------------------------------------

export interface CopilotToolCallView {
  id: string;
  name: string;
  status: 'running' | 'done' | 'error';
  summary?: string;
}

/**
 * One turn of the conversation as the UI sees it, before and after the stream ends.
 *
 * `blocks` is the render model throughout — the same block list `aiMessageSchema` carries, built
 * up in arrival order while streaming and replaced by the server's final list on `done` — so a
 * screen renders one thing whether the turn is live or finished. `text` and `toolCalls` are
 * conveniences derived from it: the text so far without walking blocks, the tool calls by id.
 * `proposals` holds the full proposal objects the `proposal` blocks refer to by id.
 */
export interface CopilotTurnView {
  turnId: string | null;
  text: string;
  blocks: AiBlock[];
  proposals: AiProposal[];
  toolCalls: CopilotToolCallView[];
  error: { code: string; message: string } | null;
  done: boolean;
}

export function emptyTurnView(): CopilotTurnView {
  return {
    turnId: null,
    text: '',
    blocks: [],
    proposals: [],
    toolCalls: [],
    error: null,
    done: false,
  };
}

function textOf(blocks: AiBlock[]): string {
  return blocks.map((block) => (block.type === 'text' ? block.text : '')).join('');
}

function toolCallsOf(blocks: AiBlock[]): CopilotToolCallView[] {
  return blocks.flatMap((block) =>
    block.type === 'tool_call'
      ? [{ id: block.id, name: block.name, status: block.status, summary: block.summary }]
      : [],
  );
}

function upsertById<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((existing) => existing.id === item.id);
  if (index === -1) return [...items, item];
  return items.map((existing, i) => (i === index ? item : existing));
}

/**
 * Pure: returns a new view, never mutates — so it drops straight into `useReducer` or a store's
 * `setState(prev => reduceCopilotEvent(prev, event))`, and a snapshot taken mid-stream stays what
 * it was.
 */
export function reduceCopilotEvent(view: CopilotTurnView, event: AiSseEvent): CopilotTurnView {
  switch (event.event) {
    case 'turn':
      return { ...view, turnId: event.data.turnId };

    case 'delta': {
      // Appends to the trailing text block, or opens one after a tool call or a card, so the final
      // list keeps the order text and tool calls actually happened in.
      const last = view.blocks.at(-1);
      const blocks =
        last?.type === 'text'
          ? [...view.blocks.slice(0, -1), { ...last, text: last.text + event.data.text }]
          : [...view.blocks, { type: 'text' as const, text: event.data.text }];
      return { ...view, text: view.text + event.data.text, blocks };
    }

    case 'tool': {
      const call: AiBlock = { type: 'tool_call', ...event.data };
      const index = view.blocks.findIndex((b) => b.type === 'tool_call' && b.id === event.data.id);
      const blocks =
        index === -1
          ? [...view.blocks, call]
          : view.blocks.map((block, i) => (i === index ? call : block));
      return { ...view, blocks, toolCalls: upsertById(view.toolCalls, event.data) };
    }

    case 'proposal': {
      const proposals = upsertById(view.proposals, event.data);
      const known = view.blocks.some(
        (b) => b.type === 'proposal' && b.proposalId === event.data.id,
      );
      const blocks = known
        ? view.blocks
        : [...view.blocks, { type: 'proposal' as const, proposalId: event.data.id }];
      return { ...view, proposals, blocks };
    }

    case 'navigate':
      return {
        ...view,
        blocks: [...view.blocks, { type: 'navigate', navigation: event.data }],
      };

    case 'question':
      return {
        ...view,
        blocks: [...view.blocks, { type: 'question', question: event.data }],
      };

    case 'done': {
      // The server's message is canonical; whatever streaming built is replaced, not merged.
      const { blocks } = event.data.message;
      return {
        ...view,
        turnId: event.data.turn.id,
        text: textOf(blocks),
        blocks,
        proposals: event.data.proposals,
        toolCalls: toolCallsOf(blocks),
        done: true,
      };
    }

    case 'error':
      return { ...view, error: event.data, done: true };
  }
}
